import * as React from "react"
import { FormEventHandler, forwardRef, Fragment } from "react"
import styled, { PolymorphicComponentProps } from "styled-components"
import { IStyledComponent, StyledTarget, Substitute } from "styled-components/dist/types"
import { CheckIcon } from "../../icons/Check"
import { CloseFilledIcon } from "../../icons/CloseFilled"
import { Typography, TypographyProps, TypographyVariant } from "../../typography"
import { InputDefault } from "./type/default"
import { PhoneIcon, PhoneInput, PhoneInputContainer } from "./type/phone"
import { Select } from "./type/select"
import { Textarea } from "./type/textarea"

export const DeprecatedLabel = styled(Typography).attrs<{
  $variant?: TypographyVariant
  $clickable?: boolean
}>((props) => ({ as: props.as ?? "label", $variant: props.$variant ?? "body-4-medium" }))<{
  $isInError?: boolean
}>`
  ${({ $isInError, theme }) => ($isInError ? `color: ${theme.colors.danger.$200};` : "")}
  ${({ $clickable }) => ($clickable ? "cursor: pointer;" : "")}
  display: flex;
  flex-direction: column;
  padding: 0;
`

type HackToBeAbleToBuildFieldError = IStyledComponent<
  "web",
  Substitute<
    React.DetailedHTMLProps<React.HTMLAttributes<HTMLParagraphElement>, HTMLParagraphElement>,
    TypographyProps & { as?: StyledTarget<"web"> }
  >
>

export const DeprecatedFieldError: HackToBeAbleToBuildFieldError = styled(Typography).attrs((props) => ({
  as: props.as ?? "strong",
  $variant: "caption-2",
}))`
  color: ${({ theme }) => theme.colors.danger.$200};
  display: inline-block;
  overflow: hidden;
  margin-top: 0.1rem;
  margin-bottom: 0.4rem;
  min-height: 1.125rem;
`

export type DeprecatedFieldProps<C extends React.ElementType> = PolymorphicComponentProps<
  "web",
  {
    fieldLabel?: React.ReactNode
    errorMessage?: string
    inline?: boolean
    inputAs?: React.ElementType
    errorAs?: React.ElementType
    containerProps?: Partial<HTMLLabelElement>
    defaultCountry?: string
    $fullWidth?: boolean
    "aria-invalid"?: boolean | "true" | "false" | "grammar" | "spelling"
    height?: "large" | "small"
    onChange?: (value: string | null) => void
    onBlur?: FormEventHandler<HTMLInputElement>
    noErrorMessage?: boolean
    rightIcon?: React.ReactNode
    leftIcon?: React.ReactNode
  },
  C,
  C
>

const FieldInputCmp = <C extends React.ElementType>(
  {
    errorMessage,
    as,
    type,
    inputAs,
    $fullWidth,
    onChange,
    onBlur,
    height,
    leftIcon,
    rightIcon,
    ...props
  }: DeprecatedFieldProps<C>,
  ref?: React.ForwardedRef<HTMLInputElement>,
) => {
  const $isInError = !!errorMessage
  const $isValid = (props as unknown as DeprecatedFieldProps<C>)["aria-invalid"] === false

  if (type === "tel") {
    return (
      <PhoneInputContainer $fullWidth={$fullWidth} className="krypton_phoneInputContainer">
        <PhoneInput
          $fullWidth={$fullWidth}
          type={type}
          {...props}
          onBlur={(e) => {
            if (onBlur) {
              onBlur({
                ...e,
                target: {
                  ...e.target,
                  value: e.target.value ?? null,
                },
              })
            }
          }}
          onChange={(value: string | React.FormEvent<HTMLInputElement>) => {
            const cb = onChange
            if (cb) {
              cb(value ?? null)
            }
          }}
          $isInError={$isInError}
          ref={ref}
        />
        <PhoneIcon as={$isInError ? CloseFilledIcon : CheckIcon} $isValid={$isValid} $isInError={$isInError} />
      </PhoneInputContainer>
    )
  }

  // Create a container for the input and icons when icons are present
  if (leftIcon || rightIcon) {
    return (
      <div
        style={{
          position: "relative",
          width: $fullWidth ? "100%" : undefined,
          display: "flex",
          alignItems: "center",
        }}
      >
        {leftIcon && (
          <div
            style={{
              position: "absolute",
              left: "10px",
              display: "flex",
              alignItems: "center",
            }}
          >
            {leftIcon}
          </div>
        )}
        <InputDefault
          type={type}
          as={inputAs}
          {...props}
          $isInError={$isInError}
          $fullWidth={$fullWidth}
          ref={ref}
          $height={height}
          onChange={onChange}
          onBlur={onBlur}
          style={{
            ...(leftIcon ? { paddingLeft: "36px" } : {}),
            ...(rightIcon ? { paddingRight: "36px" } : {}),
          }}
          onWheel={(e) => {
            const target = e.target as HTMLInputElement

            /*
             ** This is a hack to prevent the default behavior of the mousewheel event on input[type="number"]
             ** where it will increment/decrement the value of the input and prevent the user from scrolling the page.
             ** More explanation here: https://stackoverflow.com/questions/9712295/disable-scrolling-on-input-type-number
             */
            if (target.type === "number") {
              target.blur()

              e.stopPropagation()

              setTimeout(() => {
                target.focus()
              }, 0)
            }
          }}
        />
        {rightIcon && (
          <div
            style={{
              position: "absolute",
              right: "10px",
              display: "flex",
              alignItems: "center",
              pointerEvents: "none",
            }}
          >
            {rightIcon}
          </div>
        )}
      </div>
    )
  }

  // Original input without icons
  return (
    <InputDefault
      type={type}
      as={inputAs}
      {...props}
      $isInError={$isInError}
      $fullWidth={$fullWidth}
      ref={ref}
      $height={height}
      onChange={onChange}
      onBlur={onBlur}
      onWheel={(e) => {
        const target = e.target as HTMLInputElement

        /*
         ** This is a hack to prevent the default behavior of the mousewheel event on input[type="number"]
         ** where it will increment/decrement the value of the input and prevent the user from scrolling the page.
         ** More explanation here: https://stackoverflow.com/questions/9712295/disable-scrolling-on-input-type-number
         */
        if (target.type === "number") {
          target.blur()

          e.stopPropagation()

          setTimeout(() => {
            target.focus()
          }, 0)
        }
      }}
    />
  )
}

export const DeprecatedFieldInput = forwardRef(FieldInputCmp)

const FieldCmp = <C extends React.ElementType = "input">(
  {
    fieldLabel,
    errorMessage,
    as,
    inputAs,
    errorAs,
    inline,
    noErrorMessage,
    containerProps = {},
    ...props
  }: DeprecatedFieldProps<C>,
  ref?: React.ForwardedRef<HTMLInputElement>,
) => {
  const LabelAndInputContainer = inline ? "div" : Fragment
  return (
    <DeprecatedLabel {...containerProps} as={as}>
      <LabelAndInputContainer>
        {fieldLabel}
        <DeprecatedFieldInput inline={inline} errorMessage={errorMessage} {...props} ref={ref} />
      </LabelAndInputContainer>
      {!noErrorMessage && <DeprecatedFieldError as={errorAs}>{errorMessage}</DeprecatedFieldError>}
    </DeprecatedLabel>
  )
}

export const DeprecatedField = forwardRef(FieldCmp)

export const DeprecatedFieldSelectCmp = <C extends React.ElementType>(
  {
    fieldLabel,
    errorMessage,
    as,
    inputAs,
    errorAs,
    containerProps = {},
    children,
    $fullWidth,
    height,
    noErrorMessage,
    ...selectProps
  }: DeprecatedFieldProps<C>,
  ref?: React.ForwardedRef<HTMLInputElement>,
) => {
  const $isInError = !!errorMessage

  return (
    <DeprecatedLabel {...containerProps} as={as}>
      {fieldLabel}
      <Select ref={ref} {...selectProps} as={inputAs} $isInError={$isInError} $fullWidth={$fullWidth} $height={height}>
        {children}
      </Select>
      {!noErrorMessage && <DeprecatedFieldError as={errorAs}>{errorMessage}</DeprecatedFieldError>}
    </DeprecatedLabel>
  )
}

export const DeprecatedFieldSelect = forwardRef(DeprecatedFieldSelectCmp) as <C extends React.ElementType>(
  props: DeprecatedFieldProps<C> & { ref?: React.ForwardedRef<HTMLInputElement> },
) => ReturnType<typeof DeprecatedFieldSelectCmp>

const CheckboxFieldSet = styled.fieldset`
  border: none;
  padding: 0;
  margin: 0;
`

export type DeprecatedFieldListProps = {
  fieldLabel: React.ReactNode
  errorMessage?: string
  as?: React.ElementType
  legendAs?: React.ElementType
  errorAs?: React.ElementType
  legendProps?: PolymorphicComponentProps<"web", TypographyProps, "legend", "legend">
} & React.HTMLProps<HTMLFieldSetElement>

export const DeprecatedFieldList: React.FC<DeprecatedFieldListProps> = ({
  fieldLabel,
  errorMessage,
  as,
  legendAs,
  errorAs,
  legendProps = {},
  children,
  ...fieldSetProps
}) => {
  const $isInError = !!errorMessage

  return (
    <CheckboxFieldSet as={as} {...fieldSetProps}>
      <DeprecatedLabel $clickable={true} {...legendProps} $isInError={$isInError} as={legendAs ?? "legend"}>
        {fieldLabel}
      </DeprecatedLabel>
      {children}
      <DeprecatedFieldError as={errorAs}>{errorMessage}</DeprecatedFieldError>
    </CheckboxFieldSet>
  )
}
export { Checkbox as DeprecatedCheckbox } from "./type/checkbox"
export { Radio as DeprecatedRadio } from "./type/radio"
export { Textarea as DeprecatedTextarea } from "./type/textarea"

export type DeprecatedFieldTextareaProps = {
  fieldLabel: React.ReactNode
  errorMessage?: string
  as?: React.ElementType
  inputAs?: React.ElementType
  errorAs?: React.ElementType
  containerProps?: Partial<HTMLLabelElement>
  fullWidth?: boolean
} & React.HTMLProps<HTMLTextAreaElement>

export const DeprecatedFieldTextarea = forwardRef(
  (
    {
      fieldLabel,
      errorMessage,
      as,
      inputAs,
      errorAs,
      children,
      fullWidth,
      containerProps = {},
      ...textareaProps
    }: DeprecatedFieldTextareaProps,
    ref?: React.ForwardedRef<HTMLInputElement>,
  ) => {
    const $isInError = !!errorMessage

    return (
      <DeprecatedLabel {...containerProps} as={as}>
        {fieldLabel}
        <Textarea ref={ref} as={inputAs} $isInError={$isInError} $fullWidth={fullWidth} {...textareaProps} />
        <DeprecatedFieldError as={errorAs}>{errorMessage}</DeprecatedFieldError>
      </DeprecatedLabel>
    )
  },
)
